import React, { useEffect, useMemo, useState } from "react";
import { LinearProgress } from '@mui/joy';
import { useDataProvider } from "src/mtska-frontend-data-provider";
import { useTranslation } from 'react-i18next';

const PivotTable = ({
    dataProviderName,
    filters,
    compareFilters = null,
    rows = [],
    values = [],
    columns = []
}) => {
    const { t } = useTranslation();
    const valueFormatter = (v) => {
        if (v == null || Number.isNaN(Number(v))) {
            return "–";
        }
        const num = Number(v);
        return num === 0 ? "–" : num.toLocaleString("it-IT");
    };

    const useHeatmap = (data) => {
        return useMemo(() => {
            if (!data || !data.columns) return {};
            const maxByColumn = {};
            data.columns.forEach((col) => {
                let max = 0;
                (data.rows || []).forEach((row) => {
                    const children = row?.children || [];
                    // valori di dettaglio (secondo livello)
                    children.forEach((child) => {
                        const v = child.values?.[col] ?? 0;
                        if (v > max) max = v;
                    });
                    // valori aggregati del gruppo (primo livello)
                    const groupValue = row.values?.[col] ?? 0;
                    if (groupValue > max) max = groupValue;
                });
                maxByColumn[col] = max;
            });
            return maxByColumn;
        }, [data]);
    }

    const [drawable, setDrawable] = useState();
    const [data, setData] = useState({});
    const [knownFilters, setKnownFilters] = useState();
    // livello massimo di profondità visibile (0 = solo riga principale)
    const [maxVisibleDepth, setMaxVisibleDepth] = useState(0);

    const { items, loadAll, reloadCounter } = useDataProvider(dataProviderName);

    useEffect(() => {
        const shouldReload = (JSON.stringify(filters) != knownFilters) || (typeof filters == typeof undefined && items.length == 0);
        if (shouldReload) {
            setKnownFilters(JSON.stringify(filters));
            loadAll(filters, null, null, null, {
                rows,
                values,
                columns,
                compareFilters
            });
        }
    }, [reloadCounter, filters, rows, values, columns, compareFilters]);

    useEffect(() => {
        if (!items) return;

        if (Array.isArray(items)) return;
        setData(items);
        setDrawable(true);
    }, [items]);

    const maxByColumn = useHeatmap(data);
    const { columns: dataColumns = [], rows: dataRows = [], grandTotal = { values: {}, total: 0 } } = data;

    return (!drawable ? <LinearProgress /> :
        <div className="mtska-pivot-wrapper">
            <div className="mtska-pivot-scroll">
                <table className="mtska-pivot-table">
                    <thead className="mtska-pivot-thead">
                        <tr className="mtska-pivot-thead__row">
                            {rows
                                .filter((_, idx) => idx <= maxVisibleDepth)
                                .map((r, idx) => {
                                    const isGroupCol = idx === 0;
                                    const originalIdx = idx;
                                    const canToggleNext = originalIdx < rows.length - 1;
                                    const nextDepth = originalIdx + 1;
                                    const isExpanded = maxVisibleDepth >= nextDepth;

                                    return (
                                        <th
                                            key={r.field}
                                            className={isGroupCol
                                                ? "mtska-pivot-thead__cell mtska-pivot-thead__cell--group"
                                                : "mtska-pivot-thead__cell mtska-pivot-thead__cell--leaf"}
                                        >
                                            {t(r.field)}
                                            {canToggleNext && (
                                                <button
                                                    className={`mtska-pivot-toggle ${isExpanded ? "" : "mtska-pivot-toggle--collapsed"}`}
                                                    onClick={() =>
                                                        setMaxVisibleDepth(prev =>
                                                            prev >= nextDepth ? originalIdx : nextDepth
                                                        )
                                                    }
                                                    title={
                                                        isExpanded
                                                            ? `Comprimi ${rows[nextDepth]?.field ?? ""}`
                                                            : `Espandi ${rows[nextDepth]?.field ?? ""}`
                                                    }
                                                >
                                                    <span className="mtska-pivot-toggle__icon">▾</span>
                                                </button>
                                            )}
                                        </th>
                                    );
                                })}
                            {dataColumns.map((col) => (
                                <th key={col} className="mtska-pivot-thead__cell mtska-pivot-thead__cell--month">
                                    {t(col)}
                                </th>
                            ))}
                            <th className="mtska-pivot-thead__cell mtska-pivot-thead__cell--total">
                                Totale
                            </th>
                        </tr>
                    </thead>

                    <tbody className="mtska-pivot-tbody">
                        {dataRows.map((row) => (
                            <PartnerRows
                                key={row.partner}
                                row={row}
                                rowsMeta={rows}
                                columns={dataColumns}
                                maxByColumn={maxByColumn}
                                valueFormatter={valueFormatter}
                                maxVisibleDepth={maxVisibleDepth}
                            />
                        ))}
                    </tbody>

                    <tfoot className="mtska-pivot-tfoot">
                        <tr className="mtska-pivot-tfoot__row">
                            <td
                                className="mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--label"
                                colSpan={Math.min(rows.length, maxVisibleDepth + 1)}
                            >
                                Totale complessivo
                            </td>
                            {dataColumns.map((col) => (
                                <td key={col} className="mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--value">
                                    {valueFormatter(grandTotal.values[col] ?? 0)}
                                </td>
                            ))}
                            <td className="mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--total">
                                {valueFormatter(grandTotal.total)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-componenti
// ---------------------------------------------------------------------------

function PartnerRows({ row, rowsMeta, columns, maxByColumn, valueFormatter, maxVisibleDepth }) {
    const children = row?.children || [];

    // Costruisce una lista piatta di tutte le righe di dettaglio (tutti i livelli di children)
    const detailRows = [];
    const collectDetailRows = (nodes, depth) => {
        (nodes || []).forEach((node) => {
            detailRows.push({ node, depth });
            if (node.children && node.children.length > 0) {
                collectDetailRows(node.children, depth + 1);
            }
        });
    };

    collectDetailRows(children, 1);

    const visibleDetailRows = detailRows.filter(({ depth }) => depth <= maxVisibleDepth);
    const visibleRowsMeta = rowsMeta.filter((_, idx) => idx <= maxVisibleDepth);

    return (
        <>
            {visibleDetailRows.map(({ node, depth }, idx) => (
                    <tr
                        key={`${row.label}__${node.label}__${idx}`}
                        className={`mtska-pivot-tbody__row mtska-pivot-tbody__row--area ${idx === 0 ? "mtska-pivot-tbody__row--first" : ""}`}
                    >
                        {visibleRowsMeta.map((_, colIdx) => {
                            const isGroupCol = colIdx === 0;
                            const isLabelCol = colIdx === depth;
                            const className = isGroupCol
                                ? "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--group"
                                : "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--leaf";
                            return (
                                <td key={colIdx} className={className}>
                                    {isLabelCol ? node.label : ''}
                                </td>
                            );
                        })}

                        {columns.map((col) => {
                            const value = node.values?.[col] ?? 0;
                            const intensity = maxByColumn[col] > 0 ? value / maxByColumn[col] : 0;
                            return (
                                <td
                                    key={col}
                                    className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--value"
                                    style={value > 0 ? { "--heat-intensity": intensity.toFixed(3) } : undefined}
                                    data-heat={value > 0 ? "true" : undefined}
                                >
                                    {valueFormatter(value)}
                                </td>
                            );
                        })}

                        <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--rowtotal">
                            {valueFormatter(node.total)}
                        </td>
                    </tr>
                ))}

            {/* Riga totale partner */}
            <tr className="mtska-pivot-tbody__row mtska-pivot-tbody__row--subtotal">
                {visibleRowsMeta.map((_, colIdx) => {
                    const isGroupCol = colIdx === 0;
                    const className = isGroupCol
                        ? "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--group"
                        : "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-label";
                    return (
                        <td key={colIdx} className={className}>
                            {isGroupCol ? row.label : colIdx === 1 ? 'Totale' : ''}
                        </td>
                    );
                })}
                {columns.map((col) => (
                    <td key={col} className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-value">
                        {valueFormatter(row.values?.[col] ?? 0)}
                    </td>
                ))}
                <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-total">
                    {valueFormatter(row.total)}
                </td>
            </tr>
        </>
    );
}

export default PivotTable;
